// assets/js/components/revenue-calculator/calc.js
//
// Ported verbatim from the spec's calcResults/buildRecs/getBenchmarks (plain
// JS in the source, not JSX) — same variable names, same formulas.
//
// See ./README.md for what each loss/recommendation category represents and
// how to safely extend buildRecs()/calcResults() — the exact math lives here.

var data = require("./data");
var HOURS_MAP = data.HOURS_MAP;
var featureLink = data.featureLink;

var ADMIN_KEYS = ["adminManual", "adminFollowup", "adminMarketing", "adminReporting", "adminForms"];

function sumAdminHours(d) {
    return ADMIN_KEYS.reduce(function (a, k) { return a + (HOURS_MAP[d[k]] || 0); }, 0);
}

function calcResults(d, path, currencySymbol) {
    var sym = currencySymbol || "$";
    var books = Number(d.bookingsMonth) || 0, avg = Number(d.avgBookingValue) || 0, bm = books * avg;
    var dur = Number(d.apptDuration) || 1, hrly = dur > 0 ? avg / dur : avg;
    var nsDef = Math.round(books * (Number(d.noShowRate) || 0.10));
    var ns = (d.noShowsMonth != null && d.noShowsMonth !== "") ? Number(d.noShowsMonth) : nsDef;
    var nsRaw = ns * avg;
    var emHrs = Number(d.emptyHours) || 0;
    var emRaw = Math.min(emHrs * 4.33 * hrly, bm * 0.60);
    var canc = Number(d.cancelations) || 0, cancRaw = canc * avg * 0.80;

    var onlineSt = [d.usesOnlineBookingWebsite, d.usesInstagramBooking, d.usesReserveWithGoogle, d.bookingSystem === "SimplyBook.me"].filter(Boolean).length;
    var obF = path === "advanced" ? (onlineSt >= 2 ? 0.01 : onlineSt === 1 ? 0.04 : 0.10) : (d.bookingSystem === "SimplyBook.me" ? 0.01 : 0.07);
    var missedL = bm * obF;

    var retL = 0;
    if (path === "advanced") {
        var nC = !d.campaignsPerMonth || d.campaignsPerMonth === "None";
        var nR = d.requestsReviews !== "Yes";
        var nLoy = !d.promotes || d.promotes.length === 0;
        retL = bm * [0.06, 0.04, 0.02, 0.005][[!nC, !nR, !nLoy].filter(Boolean).length];
    }

    var nsMit = 0, cancMit = 0;
    if (path === "advanced") {
        if (d.usesReminders) nsMit += 0.15;
        if (d.usesDeposits) nsMit += 0.15;
        if (d.usesWaitlist) cancMit += 0.20;
        if (d.usesCancelPolicy) cancMit += 0.10;
        if (d.usesDeposits) cancMit += 0.10;
        nsMit = Math.min(nsMit, 0.40);
        cancMit = Math.min(cancMit, 0.40);
    }

    var nsLoss = nsRaw * (1 - nsMit), cancLoss = cancRaw * (1 - cancMit), emLoss = emRaw;
    var total = nsLoss + emLoss + cancLoss + missedL + retL;

    var nsRec = nsLoss * 0.50;
    var wRec = !d.usesWaitlist ? cancRaw * 0.30 + emRaw * 0.15 : cancRaw * 0.10 + emRaw * 0.05;
    var oRec = onlineSt < 2 ? missedL * 0.60 + emRaw * 0.20 : missedL * 0.30;
    var schedRec = Math.min(wRec + oRec, (cancRaw + emRaw + missedL) * 0.55);

    var retRec = 0;
    if (path === "advanced") {
        var cR = (!d.campaignsPerMonth || d.campaignsPerMonth === "None") ? retL * 0.25 : 0;
        var rR = d.requestsReviews !== "Yes" ? retL * 0.15 : 0;
        var lR = (!d.promotes || d.promotes.length === 0) ? retL * 0.20 : 0;
        retRec = Math.min(cR + rR + lR, retL * 0.45);
    }
    var totalRec = Math.max(nsRec + schedRec + retRec, 0);

    var adminVal = 0;
    if (path === "advanced") {
        var hw = sumAdminHours(d);
        adminVal = hw * 4.33 * 0.35 * hrly * (Number(d.staffCount) > 1 ? 0.25 : 0.40);
    }

    var nsRate = bm > 0 ? nsRaw / bm : 0, cancRate = bm > 0 ? cancRaw / bm : 0, emRate = bm > 0 ? emRaw / bm : 0;
    var lossScore = nsRate + cancRate * 0.8 + emRate * 0.6;
    var toolScore = path === "advanced"
        ? [d.usesReminders, d.usesDeposits, d.usesWaitlist,
            d.usesOnlineBookingWebsite || d.usesInstagramBooking || d.usesReserveWithGoogle,
            d.usesForms, d.usesMessaging, d.usesCancelPolicy].filter(Boolean).length
        : 0;
    var isWellOpt = lossScore < 0.10 && path === "advanced" && toolScore >= 5;
    var hasToolsLosing = path === "advanced" && toolScore >= 4 && !isWellOpt && lossScore >= 0.10;

    return {
        sym: sym, bm: bm, ns: ns, avg: avg, dur: dur, hrly: hrly, onlineSt: onlineSt,
        nsRaw: nsRaw, nsLoss: nsLoss, nsMit: nsMit,
        emRaw: emRaw, emLoss: emLoss,
        cancRaw: cancRaw, cancLoss: cancLoss, cancMit: cancMit,
        missedL: missedL, retL: retL, total: total,
        nsRec: nsRec, schedRec: schedRec, retRec: retRec, totalRec: totalRec,
        adminVal: adminVal, lossScore: lossScore, isWellOpt: isWellOpt, hasToolsLosing: hasToolsLosing
    };
}

function buildRecs(d, r, path) {
    var bm = r.bm, promotes = d.promotes || [];
    var hw = sumAdminHours(d);

    var sig = {
        high_ns: r.nsRaw > bm * 0.05,
        high_canc: r.cancRaw > bm * 0.03,
        high_empty: r.emRaw > bm * 0.05,
        weak_online: r.onlineSt < 2,
        no_campaigns: !d.campaignsPerMonth || d.campaignsPerMonth === "None",
        no_reviews: d.requestsReviews !== "Yes",
        low_ret: path === "advanced" && r.retL > bm * 0.02,
        low_avg: Number(d.avgBookingValue) < 60,
        high_admin: path === "advanced" && hw >= 5,
        multi_loc: Number(d.locations) > 1,
        multi_staff: Number(d.staffCount) > 3
    };
    var ok = {
        reminders: !d.usesReminders, deposits: !d.usesDeposits, cancelPolicy: !d.usesCancelPolicy,
        waitlist: !d.usesWaitlist, reserveGoogle: !d.usesReserveWithGoogle, instagram: !d.usesInstagramBooking,
        promotions: true, emailMkt: !d.usesEmailSMS,
        reviews: d.requestsReviews !== "Yes" && !d.usesReviewFlow,
        loyalty: promotes.indexOf("Loyalty offers") === -1,
        membership: promotes.indexOf("Memberships") === -1,
        packages: promotes.indexOf("Packages") === -1,
        addons: true,
        giftCards: promotes.indexOf("Gift cards") === -1,
        intakeForms: !d.usesForms, calendarSync: true, multiLocation: true, multiStaff: true
    };

    var recs = [];
    if (sig.high_ns) {
        if (ok.reminders && ok.deposits) recs.push({ id: "ns_reminders_deposits", f: ["reminders", "deposits"], p: 1, html: _t("%reminders% and %deposits% work together to reduce no-shows. Automated messages keep clients informed before their appointment, and a deposit gives them a financial reason to show up.").replace("%reminders%", featureLink("reminders")).replace("%deposits%", featureLink("deposits")) });
        else if (ok.reminders) recs.push({ id: "ns_reminders", f: ["reminders"], p: 1, html: _t("%reminders% can help reduce your remaining no-show rate. Automated pre-appointment messages are one of the most effective single changes a service business can make.").replace("%reminders%", featureLink("reminders")) });
        else if (ok.deposits) recs.push({ id: "ns_deposits", f: ["deposits"], p: 1, html: _t("%deposits% collected at the time of booking increase client commitment and reduce the likelihood of a no-show.").replace("%deposits%", featureLink("deposits")) });
        else if (ok.cancelPolicy) recs.push({ id: "ns_cancel_policy", f: ["cancelPolicy"], p: 1, html: _t("With reminders and deposits already active, a well-configured %cancelPolicy% is the next step to consistently enforce your cancellation terms.").replace("%cancelPolicy%", featureLink("cancelPolicy")) });
    }
    if (sig.high_canc) {
        if (ok.waitlist && ok.cancelPolicy) recs.push({ id: "canc_waitlist_policy", f: ["waitlist", "cancelPolicy"], p: 2, html: _t("%waitlist% fills cancelled slots automatically by notifying the next available client. Paired with a clear %cancelPolicy%, it reduces both the frequency and the revenue impact of cancellations.").replace("%waitlist%", featureLink("waitlist")).replace("%cancelPolicy%", featureLink("cancelPolicy")) });
        else if (ok.waitlist) recs.push({ id: "canc_waitlist", f: ["waitlist"], p: 2, html: _t("%waitlist% helps recover revenue from cancelled appointments by automatically offering the slot to clients who are already waiting.").replace("%waitlist%", featureLink("waitlist")) });
    }
    if (sig.high_empty || sig.weak_online) {
        if (ok.reserveGoogle && ok.instagram) recs.push({ id: "reach_google_instagram", f: ["reserveGoogle", "instagram"], p: 3, html: _t("%reserveGoogle% and %instagram% extend your booking reach. Clients can book directly from Google search results or from your social media profiles, reducing friction and filling more of your available schedule.").replace("%reserveGoogle%", featureLink("reserveGoogle")).replace("%instagram%", featureLink("instagram")) });
        else if (ok.reserveGoogle) recs.push({ id: "reach_google", f: ["reserveGoogle"], p: 3, html: _t("%reserveGoogle% allows clients to book directly from Google search results, one of the lowest-friction booking channels for service businesses.").replace("%reserveGoogle%", featureLink("reserveGoogle")) });
        else if (ok.instagram) recs.push({ id: "reach_instagram", f: ["instagram"], p: 3, html: _t("%instagram% lets clients book from your social media profiles. A significant share of service clients discover businesses through social media and expect to book without leaving the platform.").replace("%instagram%", featureLink("instagram")) });
        if (sig.high_empty) recs.push({ id: "empty_promotions", f: ["promotions"], p: 3, html: _t("%promotions% targeted at quieter time slots can attract bookings into your empty schedule gaps and improve weekly utilisation.").replace("%promotions%", featureLink("promotions")) });
    }
    if (path === "advanced") {
        if (ok.emailMkt && ok.reviews) recs.push({ id: "ret_email_reviews", f: ["emailMkt", "reviews"], p: 4, html: _t("%emailMkt% campaigns and automated %reviews% both help retain past clients. Regular messages keep your business top of mind, and a growing review count improves local search visibility.").replace("%emailMkt%", featureLink("emailMkt")).replace("%reviews%", featureLink("reviews")) });
        else if (ok.emailMkt) recs.push({ id: "ret_email", f: ["emailMkt"], p: 4, html: _t("%emailMkt% campaigns are a reliable way to bring past clients back. Even one message per month has a measurable impact on repeat-booking rates.").replace("%emailMkt%", featureLink("emailMkt")) });
        else if (ok.reviews) recs.push({ id: "ret_reviews", f: ["reviews"], p: 4, html: _t("Automated %reviews% sent after each appointment are the most consistent way to build social proof and improve local search visibility.").replace("%reviews%", featureLink("reviews")) });
        if (ok.loyalty && ok.membership && sig.low_ret) recs.push({ id: "ret_loyalty_membership", f: ["loyalty", "membership"], p: 4, html: _t("A %loyalty% programme and %membership% plan both give clients an ongoing reason to return, increasing visit frequency and making monthly revenue more predictable.").replace("%loyalty%", featureLink("loyalty")).replace("%membership%", featureLink("membership")) });
        else if (ok.loyalty) recs.push({ id: "ret_loyalty", f: ["loyalty"], p: 4, html: _t("A %loyalty% programme rewards clients for repeat visits and increases the chance they choose your business over a competitor next time.").replace("%loyalty%", featureLink("loyalty")) });
    }
    if (sig.low_avg || (path === "advanced" && ok.packages)) {
        if (ok.packages && ok.addons) recs.push({ id: "value_packages_addons", f: ["packages", "addons"], p: 5, html: _t("%packages% and %addons% are practical ways to grow revenue per booking. Packages encourage clients to commit to multiple sessions upfront, while add-ons give them more to choose from at the point of booking.").replace("%packages%", featureLink("packages")).replace("%addons%", featureLink("addons")) });
        else if (ok.packages) recs.push({ id: "value_packages", f: ["packages"], p: 5, html: _t("%packages% encourage clients to pre-pay for multiple sessions, increasing upfront revenue and improving retention.").replace("%packages%", featureLink("packages")) });
    }
    if (path === "advanced" && sig.high_admin && ok.intakeForms) {
        recs.push({ id: "admin_forms_sync", f: ["intakeForms", "calendarSync"], hours: Math.round(hw), p: 6, html: _t("%intakeForms% and %calendarSync% can reduce your %hours% weekly admin hours, and calendar sync keeps your schedule aligned without manual updates.").replace("%intakeForms%", featureLink("intakeForms")).replace("%calendarSync%", featureLink("calendarSync")).replace("%hours%", String(Math.round(hw))) });
    }
    if (sig.multi_loc && ok.multiLocation) recs.push({ id: "multi_location", f: ["multiLocation"], p: 7, html: _t("%multiLocation% lets you manage all your locations from one dashboard, with unified scheduling, reporting, and client management.").replace("%multiLocation%", featureLink("multiLocation")) });
    else if (sig.multi_staff && ok.multiStaff) recs.push({ id: "multi_staff", f: ["multiStaff"], p: 7, html: _t("%multiStaff% gives each team member their own calendar and availability settings, all manageable from a single account.").replace("%multiStaff%", featureLink("multiStaff")) });

    recs.sort(function (a, b) { return a.p - b.p; });

    if (recs.length === 0) {
        var url = data.addUTM("https://simplybook.me/en/sell-more", "explore_more_ways_to_grow");
        recs.push({ id: "explore_fallback", f: [], p: 0, html: _t("Explore %link% to review your configuration and make the most of the tools you already have active.").replace("%link%", '<a href="' + url + '" target="_blank" rel="noopener noreferrer" class="rc-calc__link">SimplyBook.me</a>') });
    }
    return recs.slice(0, 6);
}

// The business type is inserted mid-sentence ("for hair salon businesses"), so its first
// letter is lowered — but not in German, where every noun is capitalised ("Friseursalon").
function inlineType(label) {
    var lang = typeof document !== "undefined" ? (document.documentElement.getAttribute("lang") || "") : "";
    if (/^de\b/i.test(lang)) return label;
    return label.charAt(0).toLowerCase() + label.slice(1);
}

function getBenchmarks(d, r, path) {
    var type = inlineType(d.businessType ? _t(d.businessType) : _t("business"));
    var country = d.countryName || _t("your region");
    var highNS = Number(d.noShowRate) || 0.10;
    var nsRate = r.bm > 0 ? r.nsRaw / r.bm : 0;
    var lines = [];

    if (r.nsRaw <= 0) {
        lines.push(_t("You have not reported any no-shows. Automated reminders help keep it that way as your bookings grow."));
    } else if (nsRate > highNS * 1.2) {
        lines.push(_t("Your no-show rate appears higher than the typical range for %type% businesses in %country%. The category average is around %pct%%. Automated reminders and deposits are the most effective tools to bring this down.")
            .replace("%type%", type).replace("%country%", country).replace("%pct%", String(Math.round(highNS * 100))));
    } else {
        lines.push(_t("Your no-show rate is within the typical range for %type% businesses. Combining reminders with deposit requirements can reduce no-shows by up to 50%.").replace("%type%", type));
    }

    if (r.onlineSt < 2) {
        lines.push(_t("Most %type% businesses in %country% that have enabled multiple online booking channels report more total bookings, especially from clients who book outside business hours.").replace("%type%", type).replace("%country%", country));
    } else {
        lines.push(_t("Having multiple active booking channels puts you ahead of many %type% businesses that still rely on phone or manual scheduling.").replace("%type%", type));
    }

    if (path === "advanced") {
        if (d.requestsReviews !== "Yes") {
            lines.push(_t("In this sector, businesses that actively request reviews build review volume significantly faster. Higher review counts directly improve conversion from Google search and Maps."));
        }
        if (!d.campaignsPerMonth || d.campaignsPerMonth === "None") {
            lines.push(_t("Businesses that send at least one marketing campaign per month consistently show stronger repeat-booking rates. Even a single monthly message to past clients has a measurable impact on retention."));
        }
    }
    return lines;
}

// Language-independent description of the benchmark lines getBenchmarks() renders,
// posted with the lead so the report email can be rendered server-side from an
// allow-list instead of accepting client HTML. Keep the conditions in sync with
// getBenchmarks() above.
function getBenchmarkSpec(d, r, path) {
    var highNS = Number(d.noShowRate) || 0.10;
    var nsRate = r.bm > 0 ? r.nsRaw / r.bm : 0;
    var spec = [];
    if (r.nsRaw <= 0) spec.push({ id: "noshow_none" });
    else if (nsRate > highNS * 1.2) spec.push({ id: "noshow_high", pct: Math.round(highNS * 100) });
    else spec.push({ id: "noshow_ok" });
    spec.push({ id: r.onlineSt < 2 ? "channels_few" : "channels_many" });
    if (path === "advanced") {
        if (d.requestsReviews !== "Yes") spec.push({ id: "reviews_missing" });
        if (!d.campaignsPerMonth || d.campaignsPerMonth === "None") spec.push({ id: "campaigns_missing" });
    }
    return spec;
}

module.exports = { getBenchmarkSpec: getBenchmarkSpec, calcResults: calcResults, buildRecs: buildRecs, getBenchmarks: getBenchmarks, sumAdminHours: sumAdminHours };
